import { Helper } from '@/services/helper/helper'
import { AdServiceHelper } from '@/services/helper/adServiceHelper'
import store from '@/store/AppStore'

/**
 * Wrapper for functions used in TrackLib.
 */
class TrackingWrapper {
  /**
   * Sets trackMe field with value from window.piTracking.trackMe.
   * @see {@link TrackingWrapper#init}
   */
  constructor () {
    this.trackMe = window.piTracking?.trackMe || function () {}
    this.site = window.PageConfig.site === 'hp' ? 'homepage' : 'logout'

    this.init()
  }

  /**
   * Initializes window.SiteInfo with empty object if not already initialized and sets window.SiteInfo.TrackLibPageId.
   * @listens Document#pageReady
   * @see {@link TrackingWrapper#trackTeaserPixel}
   * @see {@link TrackingWrapper#setBlockNumber}
   * @see {@link TrackingWrapper#addTeaserHashTracking}
   * @see {@link TrackingWrapper#addBlockNavigationHashTracking}
   * @see {@link TrackingWrapper#addSimpleHashTracking}
   */
  init () {
    if (!window.SiteInfo) {
      window.SiteInfo = {}
    }

    if (!window.SiteInfo.TrackLibPageId) {
      window.SiteInfo.TrackLibPageId = Date.now()
    }

    store.watch(
      (state) => state.pageVisible,
      () => {
        this.trackTeaserPixel()
      }
    )

    if (window.PageConfig.site === 'hp') {
      AdServiceHelper.listenEvent('adblock.detectionfinished', (event) => {
        this.setBlockNumber()
      })
    } else {
      document.addEventListener('DOMChanged', (event) => {
        this.setBlockNumber()
      })
    }

    document.addEventListener('layerOpened', (event) => {
      this.setBlockNumber()
    })

    document.addEventListener('mousedown', (event) => {
      if (Helper.getElementByAttribute(event.target, '[data-component="footer"]')) {
        return
      }

      let element = Helper.getElementByAttribute(event.target, '[data-component="teaser"]')
      const isNativeTeaser = !!Helper.getElementByAttribute(element, '[data-service-slot]')
      if (element && !isNativeTeaser) {
        this.addTeaserHashTracking(element)
        return
      }

      element = Helper.getElementByAttribute(event.target, '[data-component="block-navigation"]')
      if (element) {
        this.addBlockNavigationHashTracking(element, event.target)
        return
      }

      element = Helper.getElementByAttribute(event.target, '[data-component="simple"]')?.parentElement
      const isSimpleAd = !!Helper.getElementByAttribute(element, '[data-service-slot]')
      const loginElement = Helper.getElementByAttribute(event.target, '[data-component="login"]')
      if (element && element.getAttribute('href') && !loginElement && !isSimpleAd) {
        this.addSimpleHashTracking(element)
        return
      }

      element = Helper.getElementByAttribute(event.target, '[data-indicator]')
      if (element) {
        this.addMiniPictureHashTracking(element.firstChild)
      }
    })
  }

  /**
   * Sets attribute data-block-number on all blocks in DOM.
   * @see {@link TrackingWrapper#setTeaserNumber};
   */
  setBlockNumber () {
    let count = 0
    const containerStructure = this.site === 'homepage' ? '' : '[data-root-container] > '
    const wallpaperPosition = document.querySelector('[data-service-slot="top"]')
    if (wallpaperPosition) {
      count++
      wallpaperPosition.setAttribute('data-block-number', count)
    }

    count++
    document.querySelector('.main-content > [data-component="header"]').setAttribute('data-block-number', count)

    const billboardPosition = document.querySelector('.main-content > [data-service-slot="middle"]')
    if (billboardPosition) {
      count++
      billboardPosition.setAttribute('data-block-number', count)
    }

    const contentLogin = document.querySelector('[data-content-login]')
    if (contentLogin) {
      count++
      contentLogin.setAttribute('data-block-number', count)
    }

    const sections = document.querySelectorAll(`.main-content > [data-content] > ${containerStructure}section`)
    sections.forEach((element) => {
      count++
      element.setAttribute('data-block-number', count)

      const parentBlockNavigation = element.previousElementSibling
      if (parentBlockNavigation) {
        const matches = parentBlockNavigation.matches ? parentBlockNavigation.matches('[data-component="block-navigation"]') : parentBlockNavigation.msMatchesSelector('[data-component="block-navigation"]')
        if (matches) {
          parentBlockNavigation.setAttribute('data-block-number', count)
        }
      }

      this.setTeaserNumber(element)
    })

    const inactivityDialog = document.querySelector('[data-tracking-name="inactivityDialog"]')
    if (inactivityDialog) {
      count++
      this.setTeaserNumber(inactivityDialog)
      inactivityDialog.setAttribute('data-block-number', count)
    }
  }

  /**
   * Sets attribute data-teaser-number for Teasers in given Block.
   * @param {Element} blockElement - Element with Teasers receiving the attribute.
   */
  setTeaserNumber (blockElement) {
    // if teaser is in pointOfView section -> start at 0
    const allTeasers = blockElement.querySelectorAll('[data-component="teaser"]')

    if (allTeasers.length) {
      let checkPointOfView = false
      let leftSlider = false
      let indexRemoval = 0

      if (Helper.getElementByAttribute(allTeasers[0], '[data-tracking-name="viewport"]')) {
        checkPointOfView = true
      }

      allTeasers.forEach((teaser, index) => {
        if (checkPointOfView && !leftSlider) {
          if (!Helper.getElementByAttribute(teaser, '[data-component="slider"]')) {
            leftSlider = true
            indexRemoval = index
          }
        }

        teaser.setAttribute('data-teaser-number', index - indexRemoval)
      })
    }
  }

  /**
   * Fires Pixel providing information of teasers in viewport.
   * @see {@link Helper.isElementVisible}
   * @see {@link Helper.getParamsAsString}
   * @see {@link TrackingWrapper#trackImage}
   */
  trackTeaserPixel () {
    const sliderTeasers = Array.from(document.querySelectorAll('[data-tracking-name="viewport"] [data-component="slider"] [data-component="teaser"]'))
    const otherViewportTeasers = Array.from(document.querySelectorAll('[data-tracking-name="viewport"] [data-component="list"] [data-component="teaser"], [data-tracking-name="viewport"] > [data-component="teaser"]'))
    const recommendationTeasers = Array.from(document.querySelectorAll('[data-tracking-name="threeList"] [data-component="teaser"]'))

    // There are hidden teasers in the viewport for other colcases. We should not count them of course
    const otherVisibleViewportTeasers = otherViewportTeasers.filter((element) => Helper.isElementVisible(element))
    const recommendationVisibleTeasers = recommendationTeasers.filter((element) => Helper.isElementVisible(element))

    const teaserTrack = []

    // add 'hero' to everything in slider
    sliderTeasers.forEach((teaser, index) => {
      teaserTrack.push('hero,' + index + ',' + teaser.getAttribute('data-teaser-id'))
    })

    // add pointOfViewTeaser to everything else in viewport
    otherVisibleViewportTeasers.forEach((teaser, index) => {
      const appendReco = this.getReco() && teaser.getAttribute('data-reco-teaser') === '1' ? '_treatSimple' : ''
      teaserTrack.push('pointOfViewTeaser' + appendReco + ',' + index + ',' + teaser.getAttribute('data-teaser-id'))
    })

    recommendationVisibleTeasers.forEach((teaser, index) => {
      const appendReco = this.getReco() && teaser.getAttribute('data-reco-teaser') === '1' ? '_treatSimple' : ''
      teaserTrack.push('Recommendation' + appendReco + ',' + index + ',' + teaser.getAttribute('data-teaser-id'))
    })

    const teaserTrackString = teaserTrack.join('|')
    const currentEvent = store.state.adData.type

    let brandInTracking = window.SiteInfo.portalName
    if (brandInTracking === 'gmxde') {
      brandInTracking = 'gmx'
    }

    let brandInTrackingPath = window.SiteInfo.portalName
    if (brandInTrackingPath.indexOf('gmx') > -1) {
      brandInTrackingPath = 'gmx'
    }

    const paramObject = {
      name: `${this.site}.startseite.event.${currentEvent}`,
      portal: brandInTracking,
      country: window.PageConfig.country,
      pvid: window.SiteInfo.TrackLibPageId,
      login: 0, // ask
      var: document.querySelector('html').getAttribute('data-variant-name'),
      type: 'hidden', // ask
      teaser: teaserTrackString,
      reco: window.PageConfig.reco,
      ts: window.startupTS
    }

    try {
      window.__tcfapi(
        'getPermission',
        2,
        (valid) => {
          if (!valid) {
            if (Object.prototype.hasOwnProperty.call(paramObject, 'pvid')) {
              paramObject.pvid = ''
            }
            if (Object.prototype.hasOwnProperty.call(paramObject, 'var')) {
              paramObject.var = ''
            }
          }
        },
        'brainTracking'
      )
    } catch (e) {}

    const paramString = Helper.getParamsAsString(paramObject)
    const qaDomainAppendix = Helper.isLiveStage() ? '' : '-qa'
    const waDomain = `https://wa${qaDomainAppendix}.${window.SiteInfo.brandShort}.${window.SiteInfo.tld}/${brandInTrackingPath}/${brandInTrackingPath}/s?${paramString}`
    this.trackImage(waDomain)

    /*
        How it should be done, when TrackLib works (maybe some adjustments vor keys)

        const trackObject = {
      name: `${this.site}.startseite.event.${currentEvent}`,
            portal: window.SiteInfo.brandShort,
            country: window.PageConfig.country,
            cont_page_id: window.SiteInfo.TrackLibPageId,
            login: 0, // ask
            var: 'default', // ask
            type: 'hidden', // ask
            teaser: teaserTrackString,
            visit_calc: 1
        };

        this.tracker.track(trackObject);
        */
  }

  /**
   * Sets toolbar info for tracking.
   * @deprecated
   * @param {Object} trackObject - Object to be filled with information.
   */
  getAndSetToolbarInfo (trackObject) {
    const toolbarInfo = {}
    toolbarInfo.kid = document.querySelector('html[united-toolbar-campaignid]').getAttribute('united-toolbar-campaignid')
    toolbarInfo.mode = document.querySelector('html[united-toolbar-mode]').getAttribute('united-toolbar-mode')
    toolbarInfo.variant = document.querySelector('html[united-toolbar-variant]').getAttribute('united-toolbar-variant')
    toolbarInfo.brand = document.querySelector('html[united-toolbar-brand]').getAttribute('united-toolbar-brand')
    toolbarInfo.bbrowser = document.querySelector('html[united-toolbar-branded-browser]').getAttribute('united-toolbar-branded-browser')
    toolbarInfo.version = document.querySelector('html[united-toolbar-version]').getAttribute('united-toolbar-version')
    toolbarInfo.accc = document.querySelector('html[united-toolbar-accountconfigured]').getAttribute('united-toolbar-accountconfigured')

    trackObject.cont_tb_campaign = '' + toolbarInfo.kid
    trackObject.cont_tb_mode = '' + toolbarInfo.mode
    trackObject.cont_tb_variant = '' + toolbarInfo.variant
    trackObject.cont_tb_brand = '' + toolbarInfo.brand
    trackObject.cont_tb_bbrowser = '' + toolbarInfo.bbrowser
    trackObject.cont_tb_version = '' + toolbarInfo.version
    trackObject.cont_tb_accc = '' + toolbarInfo.accc
  }

  /**
   * Creates 1x1 tracking pixel image.
   * @param {string} url - URL for tracking pixel.
   */
  trackImage (url) {
    const track = new Image(1, 1)
    track.src = url
  }

  /**
   * Adds hash tracking to clicked teaser.
   * @see {@link Helper#getAttributeValue}
   * @see {@link Helper#getElementByAttribute}
   */
  addTeaserHashTracking (element) {
    if (element?.href?.includes('#')) {
      return
    }

    let blockName = Helper.getAttributeValue(element, 'data-name')
    if (blockName === 'viewport') {
      if (Helper.getElementByAttribute(element, '[data-component="slider"]')) {
        blockName = 'hero'
      } else {
        blockName = 'pointOfViewTeaser'
      }
    } else {
      blockName = this.getBaseBlockName(element)
    }

    if (this.getReco() && element.getAttribute('data-reco-teaser') === '1') {
      blockName += '_treatSimple'
    }
    let hashparam = '#.' + this.site + '.' + blockName + '.' + element.getAttribute('data-headline')?.replace(/[.|#]/g, '') + '.' + element.getAttribute('data-teaser-number')

    if (element.hasAttribute('data-ab-test-id') && element.hasAttribute('data-ab-name')) {
      hashparam += '-' + element.getAttribute('data-ab-test-id') + '-' + element.getAttribute('data-ab-name')
    }

    if (blockName.startsWith('hero')) {
      const slider = Helper.getElementByAttribute(element, '[data-component="slider"]')

      if (slider) {
        const miniPicture = slider.querySelector('[data-indicator] [href="' + element.attributes.href.value + '"]')

        if (miniPicture) {
          miniPicture.attributes.href.value = element.attributes.href.value + hashparam
        }
      }
    }

    element.href += hashparam
  }

  /**
   * Adds hash tracking to clicked mini teaser.
   * @see {@link Helper#getAttributeValue}
   * @see {@link Helper#getElementByAttribute}
   */
  addMiniPictureHashTracking (element) {
    const slider = Helper.getElementByAttribute(element, '[data-component="slider"]')
    const teaser = slider.querySelector('[data-component="teaser"][href="' + element.attributes.href.value + '"]')

    if (teaser) {
      this.addTeaserHashTracking(teaser)
    }
  }

  /**
   * Adds hash tracking to clicked BlockNavigation element.
   * @see {@link Helper#getAttributeValue}
   * @see {@link TrackingWrapper#getReco}
   */
  addBlockNavigationHashTracking (blockNavigationElement, element) {
    if (!element?.href?.includes('#')) {
      const blockNavigationElements = blockNavigationElement.querySelectorAll('a')
      let blockName = this.getBaseBlockName(element)

      if (this.getReco() && element.getAttribute('data-reco-teaser') === '1') {
        blockName += '_treatSimple'
      }
      element.href += '#.' + this.site + '.' + blockName + '.' + element.innerText.trim().replace(/[.|#]/g, '') + '.' + (Array.prototype.indexOf.call(blockNavigationElements, element) + 1)
    }
  }

  /**
   * Adds hash tracking to clicked Simple element.
   * @see {@link Helper#getAttributeValue}
   * @see {@link TrackingWrapper#getReco}
   */
  addSimpleHashTracking (simpleATag) {
    if (!simpleATag.href.includes('#')) {
      const siblings = simpleATag.parentElement.querySelectorAll('a')

      simpleATag.href += '#.' + this.site + '.' + this.getBaseBlockName(simpleATag) + '.' + simpleATag.innerText.trim() + '.' + (Array.prototype.indexOf.call(siblings, simpleATag) + 1)
    }
  }

  /**
   * Returns the basic blockname required for most tracking links
   * @param element the element for which the tracking link should be created
   * @returns {string} the basic blockname for tracking
   */
  getBaseBlockName (element) {
    return Helper.getAttributeValue(element, 'data-tracking-name')
      + '_' + Helper.getAttributeValue(element, 'data-block-number')
      + '_' + Helper.getAttributeValue(element, 'data-name')
  }

  /**
   * Returns state if recommendation engine is activated and working.
   * @returns {boolean} If recommendation engine is activated and working.
   */
  getReco () {
    // control : treatment
    return window.PageConfig.reco === 'treatment'
  }
}

export { TrackingWrapper }
